import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import api from "../api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState("login"); // login | forgot-lookup | forgot-reset
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Login fields
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  // Forgot fields
  const [forgotUser, setForgotUser] = useState("");
  const [forgotDisplayName, setForgotDisplayName] = useState("");
  const [forgotRole, setForgotRole] = useState("");
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password.trim()) {
      setError("Please enter username and password");
      return;
    }
    setLoading(true);
    try {
      const user = await login(username.trim(), password.trim());
      toast.success(`Welcome, ${user.full_name}!`);
      navigate(user.role === "customer" ? "/app/customer-portal" : "/app/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response) {
        // Server responded with an error status
        if (err.response.status === 423) {
          setError(detail || "Account is locked. Please try again later.");
        } else if (err.response.status === 401) {
          setError(detail || "Invalid username or password");
        } else {
          setError(detail || "Login failed. Please try again.");
        }
      } else if (err.code === "ECONNABORTED") {
        setError("Request timed out. Please try again.");
      } else {
        setError("Unable to connect to server. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotLookup = async (e) => {
    e.preventDefault();
    setError("");
    if (!forgotUser.trim()) { setError("Enter your username, email, or phone"); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { username: forgotUser.trim() });
      setForgotUser(res.data.username);
      setForgotDisplayName(res.data.display_name || res.data.username);
      setForgotRole(res.data.role || "");
      setView("forgot-reset");
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "No account found. Try your username, email, or phone number.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    setError("");
    if (!oldPass) { setError("Enter your previous password"); return; }
    if (!newPass || !confirmPass) { setError("Enter and confirm your new password"); return; }
    if (newPass !== confirmPass) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/verify", {
        username: forgotUser, old_password: oldPass,
        new_password: newPass, confirm_password: confirmPass,
      });
      toast.success("Password reset! Sign in with your new password.");
      goToLogin();
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    setView("login");
    setError("");
    setForgotUser("");
    setForgotDisplayName("");
    setForgotRole("");
    setOldPass("");
    setNewPass("");
    setConfirmPass("");
  };

  const EyeOpen = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
  );
  const EyeClosed = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
  );
  const ErrorIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#ef4444" strokeWidth="1.5"/><path d="M8 5v3M8 10.5v.5" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/></svg>
  );

  const inputCls = "w-full h-11 px-4 rounded-lg border border-slate-700 bg-slate-800/50 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all";
  const btnCls = "w-full h-11 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2";
  const Spinner = () => <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />;

  return (
    <div className="min-h-screen flex" style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)" }}>
      {/* Left branding */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] p-12 relative">
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="h-10 w-10 rounded-lg bg-blue-500 flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <span className="font-bold text-white text-xl tracking-tight">Shubh Finance</span>
          </div>
          <h1 className="font-bold text-5xl text-white leading-[1.15] mb-5">Trusted lending,<br />simplified.</h1>
          <p className="text-slate-400 text-base leading-relaxed max-w-sm">
            Complete loan lifecycle management from customer onboarding to EMI collection, all in one place.
          </p>
        </div>
        <div className="relative z-10 space-y-3">
          {["Multi-role access control", "Smart bulk import with field mapping", "Real-time overdue & collection tracking", "Automated EMI scheduling"].map((f, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 rounded-full bg-blue-400" />
              <span className="text-slate-500 text-sm">{f}</span>
            </div>
          ))}
        </div>
        <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-slate-700/50 to-transparent" />
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="h-9 w-9 rounded-lg bg-blue-500 flex items-center justify-center">
              <span className="text-white font-bold text-base">S</span>
            </div>
            <span className="font-bold text-white text-lg">Shubh Finance</span>
          </div>

          {/* ── LOGIN ── */}
          {view === "login" && (
            <>
              <div className="mb-8">
                <h2 className="font-bold text-2xl text-white mb-1">Sign in</h2>
                <p className="text-slate-500 text-sm">Enter your credentials to access your account</p>
              </div>
              <form data-testid="login-form" onSubmit={handleLogin} className="space-y-5">
                {error && <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="login-error"><ErrorIcon /><span className="text-red-400 text-sm">{error}</span></div>}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Username</label>
                  <input data-testid="login-username" type="text" autoComplete="username" placeholder="Phone number or username" value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Password</label>
                  <div className="relative">
                    <input data-testid="login-password" type={showPass ? "text" : "password"} autoComplete="current-password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls + " pr-11"} />
                    <button type="button" onClick={() => setShowPass(!showPass)} data-testid="toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPass ? <EyeClosed /> : <EyeOpen />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button type="button" data-testid="forgot-password-link" onClick={() => { setView("forgot-lookup"); setError(""); }}
                    className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">Forgot password?</button>
                </div>
                <button data-testid="login-submit" type="submit" disabled={loading} className={btnCls}>
                  {loading ? <><Spinner />Signing in...</> : "Sign In"}
                </button>
              </form>
            </>
          )}

          {/* ── FORGOT: LOOKUP ── */}
          {view === "forgot-lookup" && (
            <>
              <div className="mb-8">
                <h2 className="font-bold text-2xl text-white mb-1">Forgot password</h2>
                <p className="text-slate-500 text-sm">Enter your username, email, or phone to find your account</p>
              </div>
              <form data-testid="forgot-lookup-form" onSubmit={handleForgotLookup} className="space-y-5">
                {error && <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="forgot-error"><ErrorIcon /><span className="text-red-400 text-sm">{error}</span></div>}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Username / Email / Phone</label>
                  <input data-testid="forgot-username" type="text" placeholder="e.g. superadmin, admin1, agent1" value={forgotUser} onChange={(e) => setForgotUser(e.target.value)} className={inputCls} />
                </div>
                <button data-testid="forgot-lookup-submit" type="submit" disabled={loading} className={btnCls}>
                  {loading ? <><Spinner />Searching...</> : "Find My Account"}
                </button>
                <button type="button" onClick={goToLogin} data-testid="back-to-login" className="w-full text-slate-500 hover:text-slate-300 text-sm font-medium text-center">Back to Sign In</button>
              </form>
            </>
          )}

          {/* ── FORGOT: RESET ── */}
          {view === "forgot-reset" && (
            <>
              <div className="mb-6">
                <h2 className="font-bold text-2xl text-white mb-1">Reset password</h2>
                <div className="mt-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-blue-300 text-sm font-medium">{forgotDisplayName}</p>
                  <p className="text-blue-400/60 text-xs">@{forgotUser}{forgotRole ? ` · ${forgotRole}` : ""}</p>
                </div>
                <p className="text-slate-500 text-sm mt-3">Enter your previous password and set a new one</p>
              </div>
              <form data-testid="forgot-verify-form" onSubmit={handleForgotReset} className="space-y-5">
                {error && <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="forgot-error"><ErrorIcon /><span className="text-red-400 text-sm">{error}</span></div>}
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Previous Password</label>
                  <input data-testid="forgot-old-password" type="password" placeholder="Enter your last known password" value={oldPass} onChange={(e) => setOldPass(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">New Password</label>
                  <input data-testid="forgot-new-password" type="password" placeholder="Min 8 chars, letters + numbers" value={newPass} onChange={(e) => setNewPass(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Confirm New Password</label>
                  <input data-testid="forgot-confirm-password" type="password" placeholder="Re-enter new password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} className={inputCls} />
                </div>
                <button data-testid="forgot-verify-submit" type="submit" disabled={loading} className={btnCls}>
                  {loading ? <><Spinner />Resetting...</> : "Reset Password"}
                </button>
                <button type="button" onClick={goToLogin} data-testid="back-to-login" className="w-full text-slate-500 hover:text-slate-300 text-sm font-medium text-center">Back to Sign In</button>
              </form>
            </>
          )}

          <p className="text-slate-600 text-xs text-center mt-8">Shubh Finance Management System</p>
        </div>
      </div>
    </div>
  );
}
